/* @flow */

import { isLimitedAccess } from '../util/app'
import { logger } from '../util/logger'
import * as deviceActions from '../ducks/device'
import * as locationActions from '../ducks/location'
import * as locationMethodActions from '../ducks/location/methods'
import * as scheduleActions from '../ducks/schedule'
import * as userActions from '../ducks/user/creators'
import * as zoneActions from '../ducks/zone'
import debounce from 'lodash.debounce'
import { createControllerMoment } from '../util/time'

// types

import type {
  CalibrationZoneRunCompletedEvent,
  CalibrationZoneRunStoppedEvent,
  Event,
  DeviceCrudEvent,
  LocationCrudEvent,
  ScheduleCrudEvent,
  ZoneCrudEvent,
  ZoneHighFlowNoShutoffDeviceEvent,
  ZoneHighFlowShutoffDeviceEvent,
  ZoneLowFlowDeviceEvent
} from './eventTypes'

/**
 * Module for dispatching actions based on received events
 * @module ducks/eventSwitchboard
 */

const { getWateringDays } = locationMethodActions
const {
  getActiveAlerts,
  getWateringSummary,
  getDeviceDetails,
  getFutureFeedEvents,
  getState: getDeviceState
} = deviceActions

/** Table holding references to debounced calls to get device state */
const debounceEventTable: { [string]: Function } = {}

// Util ///////////////////////////////////////////////////////////////////////

/**
 * Get the current user's access token OR share code
 * @param  {object} state - App state (redux store)
 * @return {string|null} - Current user's access token OR share code
 */
const currentUserAccessTokenSelector = state => {
  if (isLimitedAccess()) {
    const USP = new URLSearchParams(window.location.search)
    return USP.get('code')
  } else {
    return state.user.META.currentUser.access_token
  }
}

/**
 * Get the current user's id
 * @param  {object} state - App state (redux store)
 * @return {string|null} - Current user's id
 */
const currentUserIdSelector = (state: Object): string =>
  state.user.META.currentUser.user_id

/**
 * Get zoneId by deviceId and zoneNumber
 * @param  {object} state - App state (redux store)
 * @param  {string} deviceId - deviceId
 * @param  {number} zoneNumber - zoneNumber
 * @return {string|null} - zoneId if found
 */
const deviceZoneNumberZoneIdSelector = (state, deviceId, zoneNumber) => {
  let zoneId = null

  // Get the devices zoneIds
  const zoneIds =
    state.device[deviceId] && state.device[deviceId].META.zoneIds
      ? state.device[deviceId].META.zoneIds
      : null

  //  Get the zones for those zoneIds
  if (zoneIds) {
    const zones = zoneIds
      .filter(zoneId => state.zone[zoneId] && state.zone[zoneId].created)
      .map(zoneId => state.zone[zoneId])

    // makes sure we have a zone for each of the device's zoneIds
    if (zones.length === zoneIds.length) {
      // Find the zone with the zoneNumber we want
      const zone = zones.find(zone => zone.zoneNumber === zoneNumber)

      // Grab the id of that zone
      zoneId = zone ? zone.id : null
    }
  }

  return zoneId
}

/**
 * make needed calls for device crud events
 * @note if this call makes a getWateringDays call, it will be for 31 days to cover the intersection of the calendar requirements
 * for FutureFeed and ScheduleCalendar
 * @param {Object} - the Redux store
 * @param {string} deviceId - the deviceId returned from the event
 * @returns {Promise} - Return value of the Promise.all of the three API calls made
 */
const handleFastTrackDeviceEvents = (
  { dispatch, getState },
  { deviceId, type }
) => {
  const {
    device: { [deviceId]: { locationId } = {} } = {},
    location: { [locationId]: { timeZone } = {} } = {}
  } = getState()
  const token = currentUserAccessTokenSelector(getState())
  // $FlowFixMe
  const EVENT_ID = type + '-' + token + '-' + deviceId

  const updates = () => {
    // $FlowFixMe
    dispatch(getDeviceState(token, deviceId))
    // Only fire the other updates if this is a real user
    if (!isLimitedAccess()) {
      // $FlowFixMe
      dispatch(getWateringSummary({ token, deviceId, timeZone }))
      // $FlowFixMe
      dispatch(getFutureFeedEvents(token, deviceId))
      if (timeZone) {
        dispatch(
          getWateringDays(
            // $FlowFixMe
            token,
            locationId,
            createControllerMoment(timeZone).startOf('day'),
            createControllerMoment(timeZone)
              .add(31, 'days')
              .endOf('day')
          )
        )
      }
    }
  }

  // If this call doesn't exist in the `debounceEventTable`, add it
  if (!debounceEventTable[EVENT_ID]) {
    debounceEventTable[EVENT_ID] = debounce(updates, 2000, {
      leading: true,
      trailing: true
    })
  }

  return debounceEventTable[EVENT_ID]()
}

/**
 * @summary Handle location crud events
 * @description Unless event action is `DELETED`, get location
 * @param  {object} store - Redux store
 * @param  {object} evt - Event
 * @return {Promise} - Result of handling
 */
const handleLocationCrudEvents = (store, evt: LocationCrudEvent) => {
  if (evt.action !== 'DELETED') {
    return currentUserAccessTokenSelector(store.getState())
      ? store.dispatch(
          locationActions.get(
            // $FlowFixMe
            currentUserAccessTokenSelector(store.getState()),
            evt.locationId
          )
        )
      : Promise.reject(
          'Tried to dispatch location/get after receiving event, but missing user data'
        )
  } else {
    return store.dispatch(locationActions.remove(evt.locationId))
  }
}

/**
 * Handle device crud events
 * @description Unless event action is `DELETED`, get device details
 * @param  {object} store - Redux store
 * @param  {object} evt - Event
 * @return {Promise} - Result of handling
 */
const handleDeviceCrudEvents = (store, evt: DeviceCrudEvent) => {
  if (evt.action !== 'DELETED') {
    return currentUserAccessTokenSelector(store.getState())
      ? store.dispatch(
          getDeviceDetails(
            // $FlowFixMe
            currentUserAccessTokenSelector(store.getState()),
            evt.deviceId
          )
        )
      : Promise.reject(
          'Tried to dispatch device/getDetails after receiving event, but missing user data'
        )
  } else {
    return store.dispatch(deviceActions.remove(evt.deviceId))
  }
}

/**
 * Handle schedule crud events
 * @description Unless event action is `DELETED`, get schedule
 * @param  {object} store - Redux store
 * @param  {object} evt - Event
 * @return {Promise} - Result of handling
 */
const handleScheduleCrudEvents = (store, evt: ScheduleCrudEvent) => {
  if (evt.action !== 'DELETED') {
    return currentUserAccessTokenSelector(store.getState())
      ? store.dispatch(
          scheduleActions.get(
            // $FlowFixMe
            currentUserAccessTokenSelector(store.getState()),
            evt.scheduleId
          )
        )
      : Promise.reject(
          'Tried to dispatch schedule/get after receiving event, but missing user data'
        )
  } else {
    return store.dispatch(
      scheduleActions.scheduleDeleteComplete({ scheduleId: evt.scheduleId })
    )
  }
}

/**
 * Handle zone calibration ended events
 * @description Re-pull the updated zone details, then update zone isCalibrating state
 * @param  {object} store - Redux store
 * @param  {object} evt - Event
 * @return {Promise} - Result of handling
 */
const handleZoneCalibrationEndedEvents = (
  store,
  evt: CalibrationZoneRunCompletedEvent | CalibrationZoneRunStoppedEvent
) => {
  const token = currentUserAccessTokenSelector(store.getState())
  const zoneId = deviceZoneNumberZoneIdSelector(
    store.getState(),
    evt.deviceId,
    evt.zoneNumber
  )

  // IF we have already loaded the zone
  if (zoneId) {
    // Re-pull the updated zone details, then update zone isCalibrating state
    // by dispatching zoneActions.zoneCalibrationEndedEvent
    if (token) {
      return store.dispatch(zoneActions.get(token, zoneId)).then(() =>
        store.dispatch(
          zoneActions.zoneCalibrationEndedEvent({
            zoneId,
            isCalibrationStopped:
              evt.type === 'CALIBRATION_ZONE_RUN_STOPPED_EVENT'
          })
        )
      )
    } else {
      return Promise.reject(
        'Tried to dispatch zone/getDetails after receiving event, but missing user data'
      )
    }
  } else {
    return Promise.reject('No matching zoneId for received device zone number')
  }
}

/**
 * Handle zone crud events
 * @description Unless event action is `DELETED`, get zone
 * @param  {object} store - Redux store
 * @param  {object} evt - Event
 * @return {Promise} - Result of handling
 */
const handleZoneCrudEvents = (store, evt: ZoneCrudEvent) => {
  if (evt.action !== 'DELETED') {
    return currentUserAccessTokenSelector(store.getState())
      ? store.dispatch(
          zoneActions.get(
            // $FlowFixMe
            currentUserAccessTokenSelector(store.getState()),
            evt.zoneId
          )
        )
      : Promise.reject(
          'Tried to dispatch zone/get after receiving event, but missing user data'
        )
  } else {
    return null
  }
}

/**
 * Handle zone flow events
 * @description get the zone, and reload the active alerts on it's controller
 * @param  {object} store - Redux store
 * @param  {object} evt - Event
 * @return {Promise} - Result of handling
 */
const handleZoneFlowEvents = (
  store,
  evt:
    | ZoneHighFlowNoShutoffDeviceEvent
    | ZoneHighFlowShutoffDeviceEvent
    | ZoneLowFlowDeviceEvent
) => {
  const token = currentUserAccessTokenSelector(store.getState())
  const zoneId = deviceZoneNumberZoneIdSelector(
    store.getState(),
    evt.deviceId,
    evt.zoneNumber
  )

  if (token) {
    const promises = []

    // Refresh the alerts on the zone's controller
    promises.push(store.dispatch(getActiveAlerts(token, evt.deviceId)))

    // If we already loaded the load, refresh it
    if (zoneId) promises.push(store.dispatch(zoneActions.get(token, zoneId)))

    return Promise.all(promises)
  }

  return Promise.reject(
    'Tried to dispatch zone/get after receiving event, but missing user data'
  )
}

// Main ///////////////////////////////////////////////////////////////////////

/**
 * Dispatch actions related to events
 * @param  {object} store - Redux store
 * @param  {object} evt - Event from server
 * @return {promise} - Dispatched action if there's a matching event type, otherwise error
 */
const eventSwitchboard = (store: Object, evt: Event = {}): Promise<any> => {
  // Make sure we're tracking events
  logger.info('NEW EVENT: ', evt)
  store.dispatch({
    type: 'RACHIO/EVENT/EVENT_FROM_SERVER',
    payload: evt
  })

  // LIMITED ACCESS ///////////////////////////////////////////////////////////
  // If limited access, only listen to fast track events
  if (isLimitedAccess()) {
    /** Error text for no matching event type */
    const NO_MATCHING_EVENT_TYPE_TEXT: string =
      'NO_MATCHING_EVENT_TYPE_LIMITED_ACCESS'

    // Switch based on `evt.type`
    switch (evt.type) {
      // Primary device update event
      // Note that this event is debounced as we get serveral (3 or 4) events
      // for every watering change; see `handleFastTrackDeviceEvents`
      case 'FAST_TRACK_DEVICE_EVENT':
        return currentUserAccessTokenSelector(store.getState())
          ? handleFastTrackDeviceEvents(store, evt)
          : Promise.reject(
              'Tried to dispatch device/getState after receiving event, but missing user data (limited access)'
            )

      default:
        logger.warn(NO_MATCHING_EVENT_TYPE_TEXT, { evt })
        return Promise.reject(new Error(NO_MATCHING_EVENT_TYPE_TEXT))
    }
    // NORMAL ACCESS //////////////////////////////////////////////////////////
  } else {
    /** Error text for no matching event type */
    const NO_MATCHING_EVENT_TYPE_TEXT: string = 'NO_MATCHING_EVENT_TYPE'

    // Switch based on `evt.type`
    switch (evt.type) {
      // Primary device update event
      // Note that this event is debounced as we get serveral (3 or 4) events
      // for every watering change; see `handleFastTrackDeviceEvents`
      case 'FAST_TRACK_DEVICE_EVENT':
        return currentUserAccessTokenSelector(store.getState())
          ? handleFastTrackDeviceEvents(store, evt)
          : Promise.reject(
              'Tried to dispatch device/getState after receiving event, but missing user data'
            )
      // Shared access
      case 'SHARED_LIMITED_ACCESS_EVENT':
      case 'SHARED_ACCESS_EVENT':
      case 'SHARED_ACCESS_WITHOUT_ACCOUNT_EVENT':
        return currentUserAccessTokenSelector(store.getState())
          ? evt.sharedWithAccountId === currentUserIdSelector(store.getState())
            ? // Shared *with* this user
              store
                .dispatch(
                  locationActions.get(
                    // $FlowFixMe
                    currentUserAccessTokenSelector(store.getState()),
                    evt.locationId
                  )
                )
                .then(() =>
                  store.dispatch(
                    userActions.addLocation({
                      // $FlowFixMe
                      userId: evt.sharedWithAccountId,
                      // $FlowFixMe
                      locationId: evt.locationId
                    })
                  )
                )
            : // Shared *by* this user
              store.dispatch(
                locationMethodActions.viewSharedLocationGrants(
                  // $FlowFixMe
                  currentUserAccessTokenSelector(store.getState()),
                  evt.locationId
                )
              )
          : Promise.reject(
              'Tried to dispatch location/viewSharedLocationGrants after receiving event, but missing user data'
            )
      case 'REVOKE_SHARED_ACCESS_EVENT':
        return currentUserAccessTokenSelector(store.getState())
          ? evt.sharedWithAccountId === currentUserIdSelector(store.getState())
            ? // Shared *with* this user
              store.dispatch(locationActions.remove(evt.locationId))
            : // Shared *by* this user
              store.dispatch(
                locationMethodActions.viewSharedLocationGrants(
                  // $FlowFixMe
                  currentUserAccessTokenSelector(store.getState()),
                  evt.locationId
                )
              )
          : Promise.reject(
              'Tried to dispatch location/viewSharedLocationGrants after receiving event, but missing user data'
            )
      case 'DEVICE_CRUD_EVENT':
        return Promise.resolve(handleDeviceCrudEvents(store, evt))
      case 'SCHEDULE_CRUD_EVENT':
        return Promise.resolve(handleScheduleCrudEvents(store, evt))
      case 'ZONE_CRUD_EVENT':
        return Promise.resolve(handleZoneCrudEvents(store, evt))
      case 'LOCATION_CRUD_EVENT':
        return Promise.resolve(handleLocationCrudEvents(store, evt))
      // Zone calibration
      case 'CALIBRATION_ZONE_RUN_STARTED_EVENT':
        // Calibration also sends a FAST_TRACK_DEVICE_EVENT
        // so we are already re-pulling device state. Do nothing here.
        return Promise.resolve()
      case 'CALIBRATION_ZONE_RUN_COMPLETED_EVENT':
      case 'CALIBRATION_ZONE_RUN_STOPPED_EVENT':
        return handleZoneCalibrationEndedEvents(store, evt)
      // Zone Flow Events
      case 'ZONE_HIGH_FLOW_NO_SHUTOFF_DEVICE_EVENT':
      case 'ZONE_HIGH_FLOW_SHUTOFF_DEVICE_EVENT':
      case 'ZONE_LOW_FLOW_DEVICE_EVENT':
        return handleZoneFlowEvents(store, evt)
      default:
        logger.warn(NO_MATCHING_EVENT_TYPE_TEXT, { evt })
        return Promise.reject(new Error(NO_MATCHING_EVENT_TYPE_TEXT))
    }
  }
}

export default eventSwitchboard
